import { CATEGORY_ARCHIVES } from "@/fixtures/ejinsight/category-archives";
import { COLUMNIST_ROSTER } from "@/fixtures/ejinsight/columnist-roster";
import { EJINSIGHT_ARTICLE_ROUTE } from "@/lib/routes";
import {
  businessSection,
  columnistBios,
  columnistImages,
  featuresSection,
  hongKongCards,
  lifestyleSection,
  worldSection,
} from "./fixtures";
import type { ArticleCardData } from "@/components/ejinsight/shared/article-card";

export type ArticleData = {
  slug: string;
  title: string;
  category: string;
  categoryHref: string;
  authorName: string;
  authorSlug: string;
  date: string;
  heroImage: string;
  body: string[];
  authorAvatar: string;
  authorBio: string;
  /** Tag pills shown under the article body; slugs match the tag listing routes. */
  tags?: { slug: string; name: string }[];
  aiSummaryBullets?: string[];
  /** Curated "More to read" cards rendered after the article. */
  related?: ArticleCardData[];
};

export const articleArticle23: ArticleData = {
  slug: "article-23",
  title: "Does Hong Kong have the right not to enact article 23 laws?",
  category: "Hong Kong",
  categoryHref: "/ejinsight/hong-kong",
  authorName: "Frank Ching",
  authorSlug: "frank-ching",
  date: "March 27, 2024 22:31",
  heroImage: "/images/ejinsight/article-23-hero.jpg",
  authorAvatar: "/images/ejinsight/author-frank-ching.jpg",
  authorBio:
    "Frank Ching opened The Wall Street Journal's Bureau in China in 1979. He is now a Hong Kong-based writer on Chinese affairs.",
  body: [
    "Now that the debate over article 23 is over, with the Safeguarding National Security Ordinance having come into effect on March 23, it may be appropriate to look at some of the less discussed issues raised in the government's consultation document.",
    "Chief Executive John Lee said in the Legislative Council after it unanimously voted in favor of the bill, “Today, the constitutional responsibility and historic mission of legislating for Article 23 of the Basic Law have finally been fulfilled.”",
    "But is that true? Article 23 of the Basic Law specifically called on Hong Kong to enact laws to prohibit “secession” and “subversion against the Central People's Government” as well as other offences. But the law that just came into effect contains no provisions against these two crimes. Why?",
    "In its consultation document, the government referred to the mainland drafted Hong Kong National Security Law, which was imposed on the HKSAR in 2020, and said:",
    "“Considering that the HKNSL has already created offences and provided for two types of acts, namely secession and subversion, we recommend that it is not necessary for the HKSAR to legislate on the offences of secession and subversion again.”",
    "So this was the government's recommendation. This suggests that the government thought it had a choice, to enact laws on these two offences or not to do so. But the Basic Law did not offer a choice. It just said: do these things.",
    "Moreover, we are told by the government that the NSL did not change the Basic Law. Thus, “the selection of the Chief Executive by universal suffrage” remains the ultimate aim, as stated in article 45, as well as the ultimate aim of “the election of all the members of the Legislative Council by universal suffrage,” as stated in article 68.",
    "This being the case, why does the Hong Kong government believe that article 23 has changed, and that it can choose not to enact laws regarding certain offences?",
    "The consultation document cites article 7 of the NSL to justify the haste with which the administration dealt with the article 23 bill. But article 7 calls for more than speed. It calls on Hong Kong to complete legislation for safeguarding national security “as stipulated in the Basic Law.” And we all know what article 23 of the Basic Law stipulates.",
    "By putting that recommendation in the consultation document, the government was asking the public for its views. But the public has no authority to decide what article 23 of the Basic Law means in the aftermath of the 2020 HKNSL. The power to interpret the Basic Law is vested in the standing committee of the National People's Congress. This is stipulated in article 159 of the Basic Law. By deciding on its own not to include certain offences mentioned in article 23, it appears, the government has interpreted the Basic Law, exercising a power that the Hong Kong government doesn't have.",
    "Did the central authorities, perhaps, give Hong Kong that leeway, to decide what article 23 literally calls for? If so, we certainly have not been told. Besides, no central authority has the power to interpret the Basic Law, other than the standing committee of the National People's Congress. And the NPCSC has not held any meeting with this issue on its agenda.",
    "Unless the Hong Kong authorities explain to the public why they had the authority to “recommend,” that is, to choose not to do something that article 23 specifies, there will be a nagging feeling that the government has not discharged its full constitutional responsibility.",
    "But article 23 did not specify a time limit. If the government realizes that its mission has not been fully accomplished, it may still be able to discharge its responsibility during the remainder of its term, or during next term, or perhaps by the next administration. But it cannot leave the issue dangling.",
    "Evidently, the Basic Law as originally drafted envisaged the concept of Hong Kong people administering Hong Kong as very much including the enactment of national security laws. Hong Kong should not stop short, using the excuse that someone else has already done it.",
    "Besides, legislating in Hong Kong for Hong Kong is different from legislating in the mainland for the city. That surely is why the drafters of the Basic Law decided in the 1980s that Hong Kong should, “on its own,” enact designated laws on national security.",
    "This was understood by those who attempted to enact article 23 legislation two decades ago. The consultation document at that time acknowledged that “the manner in which the state's sovereignty and security are protected in the Mainland and in the HKSAR may legitimately differ.” It added: “Indeed, this has to be the case given the different situations, including the respective legal framework of the Mainland and the HKSAR.”",
    "In addition, there is value for Hong Kong, a proudly capitalist society as well as an international financial center with a cosmopolitan population with guaranteed rights and freedoms, to draft laws from its very different perspective. China clearly saw value in getting the SAR's perspective in the 1980's. The city is still unique and able to make its contribution, especially when it has such a constitutional responsibility.",
  ],
  // Tags match the TAGS_BY_SLUG map in the tag listing route, so pills link to working pages.
  tags: [
    { slug: "hong-kong-politics", name: "Hong Kong politics" },
    { slug: "national-security", name: "National security" },
  ],
  aiSummaryBullets: [
    "Hong Kong's Article 23 law omits offences against secession and subversion, with the government citing the 2020 National Security Law's existing provisions.",
    "Frank Ching argues the Basic Law gives Hong Kong no such discretion — the power to interpret rests with the National People's Congress Standing Committee, which has not addressed the issue.",
    "The author suggests the city's constitutional responsibility under Article 23 may not yet be fully discharged and could be revisited in a future term.",
  ],
  related: [
    {
      title: hongKongCards[0].title,
      author: hongKongCards[0].author,
      date: "April 26, 2026",
      img: hongKongCards[0].img,
      category: "HONG KONG",
    },
    {
      title: worldSection.items[0].title,
      author: worldSection.items[0].author,
      date: worldSection.items[0].date,
      img: worldSection.items[0].img,
      category: worldSection.label,
    },
    {
      title: businessSection.items[3].title,
      author: businessSection.items[3].author,
      date: businessSection.items[3].date,
      img: businessSection.items[3].img,
      category: businessSection.label,
    },
  ],
};

export type ListArticle = {
  title: string;
  author?: string;
  date: string;
  img: string;
  href: string;
};

export const startupsCategoryArticles: ListArticle[] = [
  {
    title: "Anthropic releases new AI model",
    date: "July 12, 2023 09:10",
    img: "/images/ejinsight/cat-anthropic.jpg",
    href: EJINSIGHT_ARTICLE_ROUTE,
  },
  {
    title: "Using smart assistant to manage health",
    author: "Dr. Winnie Tang",
    date: "May 24, 2023 06:00",
    img: "/images/ejinsight/lifestyle-3.png",
    href: EJINSIGHT_ARTICLE_ROUTE,
  },
  {
    title: "The secret of staying ahead in tech world for over 50 years",
    author: "Dr. Winnie Tang",
    date: "April 18, 2023 09:20",
    img: "/images/ejinsight/columnist-michael-chugani.png",
    href: EJINSIGHT_ARTICLE_ROUTE,
  },
  {
    title: "Hong Kong start-up wins first Deignan Award",
    author: "Mark O'Neill",
    date: "March 21, 2023 11:34",
    img: "/images/ejinsight/business-1.png",
    href: EJINSIGHT_ARTICLE_ROUTE,
  },
  {
    title: "Food waste reduction alleviates landfill dilemma",
    author: "Dr. Winnie Tang",
    date: "March 09, 2023 09:33",
    img: "/images/ejinsight/lifestyle-2.png",
    href: EJINSIGHT_ARTICLE_ROUTE,
  },
  {
    title: "Cyber security startup Sandbox AQ raises US$500 million",
    date: "February 15, 2023 06:00",
    img: "/images/ejinsight/business-3.png",
    href: EJINSIGHT_ARTICLE_ROUTE,
  },
  {
    title: "Environmental performance of old buildings needs to be improved",
    author: "Dr. Winnie Tang",
    date: "February 03, 2023 10:15",
    img: "/images/ejinsight/business-2.png",
    href: EJINSIGHT_ARTICLE_ROUTE,
  },
  {
    title: "Emissions reduction a top priority for the construction industry",
    author: "Dr. Winnie Tang",
    date: "January 24, 2023 09:48",
    img: "/images/ejinsight/lifestyle-1.png",
    href: EJINSIGHT_ARTICLE_ROUTE,
  },
  {
    title: "Pros and cons of synthetic data",
    author: "Dr. Winnie Tang",
    date: "January 19, 2023 11:00",
    img: "/images/ejinsight/world-2.png",
    href: EJINSIGHT_ARTICLE_ROUTE,
  },
  {
    title: "Vista Equity to take insurtech Duck Creek private",
    date: "January 10, 2023 10:33",
    img: "/images/ejinsight/business-4.png",
    href: EJINSIGHT_ARTICLE_ROUTE,
  },
  {
    title: "Amazon strikes deal with Grubhub",
    date: "July 07, 2022 11:55",
    img: "/images/ejinsight/world-3.png",
    href: EJINSIGHT_ARTICLE_ROUTE,
  },
  {
    title: "Tapping global payment market by integrating regulatory systems",
    author: "Dr. Winnie Tang",
    date: "June 22, 2022 08:30",
    img: "/images/ejinsight/world-4.png",
    href: EJINSIGHT_ARTICLE_ROUTE,
  },
];

export type Columnist = {
  slug: string;
  name: string;
  avatar: string;
  bio: string;
  role?: string;
  /**
   * "Social proof" stats shown on the columnist hero. These would come from a
   * CMS / analytics service in production; hand-set here while the V2 redesign
   * runs against the legacy fixtures.
   */
  stats?: { followers: number; bookmarks: number; yearsWithHKEJ: number };
  articles: ListArticle[];
};

export const columnistFrankChing: Columnist = {
  slug: "frank-ching",
  name: "Frank Ching",
  avatar: "/images/ejinsight/author-frank-ching.jpg",
  bio: "Frank Ching opened The Wall Street Journal's Bureau in China in 1979. He is now a Hong Kong-based writer on Chinese affairs.",
  role: "Greater China · Hong Kong politics",
  stats: { followers: 8420, bookmarks: 1284, yearsWithHKEJ: 12 },
  articles: [
    {
      title: "Does Hong Kong have the right not to enact article 23 laws?",
      date: "September 1, 2026",
      img: "/images/ejinsight/article-23-hero.jpg",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "A proposal for the consideration of Taiwan's president-elect",
      date: "August 30, 2026",
      img: "/images/ejinsight/world-1.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "Hong Kong needs to de-emphasize national security",
      date: "August 28, 2026",
      img: "/images/ejinsight/hk-1.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "Thaw in U.S.-China ties driven by domestic issues",
      date: "August 26, 2026",
      img: "/images/ejinsight/world-2.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "Resolving Hong Kong conundrum key to US-China summit at Apec",
      date: "August 24, 2026",
      img: "/images/ejinsight/hk-3.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "Applying Chinese wisdom To resolve the Taiwan issue",
      date: "August 22, 2026",
      img: "/images/ejinsight/world-3.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "China, doubling BRICS size, challenges U.S.-led global order",
      date: "August 20, 2026",
      img: "/images/ejinsight/business-2.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "Hong Kong needs to protect its image as financial center",
      date: "August 18, 2026",
      img: "/images/ejinsight/hk-4.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "China U.S. relations: Is it possible to stop the slide?",
      date: "August 16, 2026",
      img: "/images/ejinsight/world-4.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "How fares Hong Kong 3 years after China's crackdown?",
      date: "August 14, 2026",
      img: "/images/ejinsight/hk-5.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "Ukraine and Taiwan: Can China square the circle?",
      date: "August 12, 2026",
      img: "/images/ejinsight/business-1.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "After Biden,Xi agree to fix ties, aides shouldn't get in the way",
      date: "August 10, 2026",
      img: "/images/ejinsight/business-3.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "China: Neutral on Ukraine?",
      date: "June 20, 2026",
      img: "/images/ejinsight/business-4.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "Hong Kong:A crisis averted, but will justice be seen to be done?",
      date: "May 8, 2026",
      img: "/images/ejinsight/hk-2.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "Government shows Hong Kong's Court of Final Appeal not so final",
      date: "March 27, 2026",
      img: "/images/ejinsight/lifestyle-1.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "Protests show maintaining stable US-China ties isn't easy",
      date: "February 6, 2026",
      img: "/images/ejinsight/lifestyle-3.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "China: Xi, 69, plans to stay in power till his 80's",
      date: "January 10, 2026",
      img: "/images/ejinsight/lifestyle-2.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "China and Taiwan: What role for the KMT?",
      date: "December 5, 2025",
      img: "/images/ejinsight/lifestyle-4.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "Hong Kong new leader shouts his devotion to Xi from the rooftops",
      date: "November 7, 2025",
      img: "/images/ejinsight/features-1.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
    {
      title: "For HK to improve its deteriorating image, starting point is HK",
      date: "October 2, 2025",
      img: "/images/ejinsight/world-1.png",
      href: EJINSIGHT_ARTICLE_ROUTE,
    },
  ],
};



/**
 * The home page's category sections carry real item pools; the category ROUTES
 * did not exist, so the nav's Hong Kong / Business / World / Lifestyle /
 * Features entries all 404'd. Each listing is composed from the pool that
 * already backs that section on the home page — the same approach taken when
 * seven of lj's eight category links 404'd. No content is invented: a section
 * with few items renders a short page rather than one padded with another
 * category's stories.
 *
 * `ArticleItem` (home) has no `href`; `ListArticle` (listing) requires one, so
 * the adapter supplies the article route — the same "make the card land
 * somewhere real" default `ARTICLE_DETAIL_ROUTE` documents.
 */
function fromSection(items: { title: string; author?: string; date: string; img: string }[]): ListArticle[] {
  return items.map((it) => ({ ...it, href: EJINSIGHT_ARTICLE_ROUTE }));
}

/** A category's archive, from `CATEGORY_ARCHIVES`, with the article route
 *  attached. Every row lands on the one authored article until per-article
 *  routes exist — the same rule every other card in this vertical follows. */
function archive(slug: string): ListArticle[] {
  return (CATEGORY_ARCHIVES[slug] ?? []).map((a) => ({
    title: a.title,
    author: a.author,
    date: a.date,
    img: a.img,
    href: EJINSIGHT_ARTICLE_ROUTE,
  }));
}

export const categoriesBySlug: Record<
  string,
  { label: string; description: string; articles: ListArticle[] }
> = {
  startups: {
    label: "STARTUPS",
    description:
      "Hong Kong's emerging companies, the founders behind them, and the bets investors are making on the city's tech ecosystem.",
    articles: archive("startups"),
  },
  "hong-kong": {
    label: "HONG KONG",
    description:
      "Politics, policy and daily life in the city — reported and argued by the writers who live it.",
    articles: archive("hong-kong"),
  },
  business: {
    label: "BUSINESS",
    description:
      "Markets, companies and the money moving through Hong Kong and the region.",
    articles: archive("business"),
  },
  world: {
    label: "WORLD",
    description:
      "How events beyond Hong Kong reshape the city's economy, politics and place in the region.",
    articles: archive("world"),
  },
  lifestyle: {
    label: "LIFESTYLE",
    description:
      "Culture, the arts and living well — the softer side of the city.",
    articles: archive("lifestyle"),
  },
  features: {
    label: "FEATURES",
    description:
      "Long reads, interviews and the stories behind the headlines.",
    articles: archive("features"),
  },
};

/**
 * The full columnist roster.
 *
 * `columnistsBySlug` held exactly ONE entry while the home page's rail and
 * grid linked all six — and linked them by DISPLAY NAME
 * (`/ejinsight/columnists/Brian%20Y.%20S.%20Wong`), which does not match even
 * the one entry that existed. So all six columnist links 404'd, Frank Ching's
 * included, despite his page being fully authored.
 *
 * The five missing pages are COMPOSED from pools that already exist, the same
 * approach taken when seven of lj's eight category links 404'd and again for
 * EJ Insight's own five category listings. Nothing is invented: each writer's
 * page opens with the column `columnistBios` already names as their latest,
 * followed by the section pool that matches their beat. A writer with a short
 * pool gets a short page rather than one padded with someone else's stories.
 *
 * Frank Ching keeps his hand-authored entry — it carries a real biography and
 * stats that no pool could supply.
 */

/** `Mark O'Neill` -> `mark-oneill`. Punctuation is dropped, not encoded: a
 *  slug with `%20` and `%27` in it is not a slug. */
export function columnistSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/['’.]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

/**
 * Every article in the fixtures, so a writer's page can be assembled from the
 * pieces actually ATTRIBUTED to them.
 *
 * The first cut of this composed each page from the section pool matching the
 * writer's beat and stamped their name onto every row — which relabelled other
 * people's stories under their byline (Brian Y. S. Wong's page opened with a
 * feature on pneumococcal disease). Attribution has to come from the data, not
 * from which page the row happens to be rendered on.
 */
const ALL_POOLED_ARTICLES: ListArticle[] = [
  ...hongKongCards.map((c) => ({
    title: c.title,
    author: c.author,
    date: "",
    img: c.img,
    href: EJINSIGHT_ARTICLE_ROUTE,
  })),
  ...fromSection(businessSection.items),
  ...fromSection(worldSection.items),
  ...fromSection(lifestyleSection.items),
  ...fromSection(featuresSection.items),
  ...startupsCategoryArticles,
];

/**
 * A columnist's page: the column the directory advertises as their latest,
 * followed by everything else in the fixtures carrying their byline.
 *
 * Some writers have exactly one attributed piece, so their page is short. That
 * is the correct rendering of the data — the alternative is padding a page with
 * stories someone else wrote, which is a worse defect than a thin page.
 */
/**
 * Loose title key, for de-duplication.
 *
 * `columnistBios` and the section pools carry the same story under slightly
 * different headlines ("China profits from both sides in **the** Ukraine war"
 * vs "…in Ukraine war"), so an exact-title check let both render one above the
 * other. Articles and punctuation are dropped before comparing.
 */
function titleKey(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\b(the|a|an)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * A columnist's page, from `COLUMNIST_ROSTER`.
 *
 * It used to be COMPOSED by scanning every section pool for rows carrying the
 * writer's byline. That was the honest reading of the data at the time — but
 * most writers had exactly one attributed row, so five of the six pages were a
 * single card. QA gave the six real author pages as the reference; the roster
 * carries their actual article lists, so composition is no longer needed.
 *
 * Frank Ching keeps his hand-authored entry: it carries a real biography and
 * article stats that the roster does not model, and its article list is merged
 * with the roster's so nothing he had is lost.
 */
function fromRoster(entry: (typeof COLUMNIST_ROSTER)[number]): Columnist {
  const seen = new Set<string>();
  const articles: ListArticle[] = [];
  for (const a of entry.articles) {
    const key = titleKey(a.title);
    if (seen.has(key)) continue;
    seen.add(key);
    articles.push({
      title: a.title,
      author: entry.name,
      date: a.date,
      img: a.img,
      href: EJINSIGHT_ARTICLE_ROUTE,
    });
  }
  return {
    slug: columnistSlug(entry.name),
    name: entry.name,
    avatar: entry.avatar,
    role: entry.role,
    bio: entry.bio,
    articles,
  };
}

export const columnistsBySlug: Record<string, Columnist> = Object.fromEntries(
  COLUMNIST_ROSTER.map((entry) => {
    const composed = fromRoster(entry);
    if (entry.name !== columnistFrankChing.name) return [composed.slug, composed];
    // Frank Ching's hand-authored page keeps its biography and stats; his
    // roster articles are appended to whatever it already listed.
    const seen = new Set(columnistFrankChing.articles.map((a) => titleKey(a.title)));
    return [
      composed.slug,
      {
        ...columnistFrankChing,
        articles: [
          ...columnistFrankChing.articles,
          ...composed.articles.filter((a) => !seen.has(titleKey(a.title))),
        ],
      },
    ];
  })
);
